import { AnalyticsService } from "@core/analytics/analytics-service";
import { GoogleAnalyticsTracker } from "@core/analytics/trackers/google-analytics-tracker";
import { AuthenticatedApiService } from "@core/api/authenticated-api-service";
import { ApiServiceWithErrorHandler } from "@core/api/error-handling-api-service";
import { ExternalApiService } from "@core/api/external-api-service";
import { createAxiosInstance } from "@core/api/interceptors/axios-instance";
import { MasterApiService } from "@core/api/master-api-service";
import { UnauthenticatedApiService } from "@core/api/unauthenticated-api-service";
import { NetworkDebugManager } from "@core/debug/network-debug-manager";
import { ErrorManager } from "@core/error-manager";
import { DayjsLibConfiguration } from "@core/i18n/dayjslib-configuration";
import { Translations, TranslationsManager } from "@core/i18n/translations-manager";
import { TranslationsService } from "@core/i18n/translations-service";
import { ServiceInitPriority, ServicesManager } from "@core/services/services-manager";
import { LocalStorageKeys } from "@core/storage/local-storage-keys";
import { storage } from "@core/storage/storage";
import { AnalyticsDimensionsManager } from "@domain/analytics/analytics-dimensions-manager";
import { AnalyticsRoutesManager } from "@domain/analytics/analytics-route-manager";
import { AnalyticsCategories, AnalyticsRoutes } from "@domain/analytics/analytics-types";
import { CustomDimensions } from "@domain/analytics/custom-dimensions";
import { NetworkAnalyticsManager } from "@domain/analytics/network-analytics-manager";
import { AnnouncementManager } from "@domain/announcement/announcement-manager";
import { AnnouncementReceivedService } from "@domain/announcement/announcement-received-service";
import { AnnouncementService } from "@domain/announcement/announcement-service";
import { CognitoAuthService } from "@domain/authentication/cognito/cognito-auth-service";
import { CognitoMultiInfra } from "@domain/authentication/cognito/cognito-multi-infra";
import { CognitoServiceWrapper } from "@domain/authentication/cognito/cognito-service-wrapper";
import { CognitoUserPoolProvider } from "@domain/authentication/cognito/cognito-user-pool-provider";
import { CookiebotManager } from "@domain/cookiebot/cookiebot-manager";
import { FeatureTogglesManager, FeatureTogglesRecord } from "@domain/feature-toggles/feature-toggles-manager";
import { HealthcareTeamManager } from "@domain/healthcare-team/healthcare-team-manager";
import { HealthcareTeamService } from "@domain/healthcare-team/healthcare-team-service";
import { InfraManager } from "@domain/infra/infra-manager";
import { LegalContentManager } from "@domain/legal-content/legal-content-manager";
import { LegalContentService } from "@domain/legal-content/legal-content-service";
import { LibraryFilterManager } from "@domain/library/library-filter-manager";
import { ExerciseService } from "@domain/exercise/exercise-service";
import { ExerciseFilterManager } from "@domain/exercise/exercise-filter-manager";
import { NetworkActionsErrorsManager } from "@domain/network/network-actions-errors-manager";
import { LastUnreadCounts, NotificationBadgeManager } from "@domain/notification/notification-badge-manager";
import { NotificationManager } from "@domain/notification/notification-manager";
import { NotificationService } from "@domain/notification/notification-service";
import { OnboardingManager } from "@domain/onboarding/onboarding-manager";
import { OnboardingService } from "@domain/onboarding/onboarding-service";
import { GetStartedManager } from "@domain/get-started/get-started-manager";
import { OnfidoService } from "@domain/onfido/onfido-service";
import { PatientManager } from "@domain/patient/patient-manager";
import { PatientService } from "@domain/patient/patient-service";
import { PatientProgramsManager } from "@domain/program/patient-programs-manager";
import { PatientProgramsService } from "@domain/program/patient-programs-service";
import { ProgramManager } from "@domain/program/program-manager";
import { ProgramOperationsManager } from "@domain/program/program-operations-manager";
import { ProgramService } from "@domain/program/program-service";
import { ProgressDataManager } from "@domain/progress/progress-data-manager";
import { ProgressDataService } from "@domain/progress/progress-data-service";
import { ProspectRequestManager } from "@domain/prospect-request/prospect-request-manager";
import { ProspectRequestService } from "@domain/prospect-request/prospect-request-service";
import { CountryManager } from "@domain/localization/country-manager";
import { CountryService } from "@domain/localization/country-service";
import { FastTrackRegisterManager } from "@domain/register/register-fast-track/fast-track-register-manager";
import { FastTrackRegisterService } from "@domain/register/register-fast-track/fast-track-register-service";
import { VipRegisterManager } from "@domain/register/register-vip/vip-register-manager";
import { VipRegisterService } from "@domain/register/register-vip/vip-register-service";
import { RegisterManager } from "@domain/register/register-manager";
import { RegisterService } from "@domain/register/register-service";
import { ServerConfig } from "@domain/server-config/server-config";
import { ServerConfigManager } from "@domain/server-config/server-config-manager";
import { ServerConfigService } from "@domain/server-config/server-config-service";
import { TemplateManager } from "@domain/template/template-manager";
import { TemplateOperationsManager } from "@domain/template/template-operation-manager";
import { TemplateService } from "@domain/template/template-service";
import { TutorialBannerManager } from "@domain/tutorial-banner/tutorial-banner-manager";
import { TutorialBannerService } from "@domain/tutorial-banner/tutorial-banner-service";
import { LocaleManager } from "@domain/localization/locale-manager";
import { UserBannerManager } from "@domain/user/user-banner-manager";
import { UserSurveyManager } from "@domain/user/user-survey-manager";
import { UserSurveyService } from "@domain/user/user-survey-service";
import { UserManager } from "@domain/user/user-manager";
import { UserService } from "@domain/user/user-service";
import { UserStatusManager } from "@domain/user/user-status-manager";
import { VeevaService } from "@domain/veeva/veeva-service";
import { LearnService } from "@domain/learn/learn-service";
import { LearnManager } from "@domain/learn/learn-manager";
import axios from "axios";
import { LegalDocumentsMetadata } from "@domain/legal-content/legal-content";
import { ExerciseManager } from "@domain/exercise/exercise-manager";

const masterApiService = new MasterApiService(createAxiosInstance());
const serverConfigService = new ServerConfigService(masterApiService);
export const serverConfigManager = new ServerConfigManager(
	serverConfigService,
	storage.getStore<ServerConfig, ServerConfig>(
		LocalStorageKeys.serverConfiguration,
		sc => sc,
		sc => sc,
	),
);
export const infraManager = new InfraManager(
	serverConfigManager,
	storage.getStore<string, string>(
		LocalStorageKeys.targetInfrastructureId,
		i => i,
		i => i,
	),
);

export const networkActionsErrorsManager = new NetworkActionsErrorsManager();
const cognitoServiceWrapper = new CognitoServiceWrapper(networkActionsErrorsManager);
const userPoolProvider = new CognitoUserPoolProvider(infraManager);
const cognitoMultiInfra = new CognitoMultiInfra(cognitoServiceWrapper, userPoolProvider);
const cognitoAuthService = new CognitoAuthService(
	cognitoServiceWrapper,
	userPoolProvider,
	networkActionsErrorsManager,
	cognitoMultiInfra,
);

export const featureTogglesManager = new FeatureTogglesManager(
	storage.getStore<FeatureTogglesRecord, FeatureTogglesRecord>(
		LocalStorageKeys.featureToggles,
		i => i,
		i => i,
	),
);

export const googleAnalyticsTracker = new GoogleAnalyticsTracker();
export const analyticsService = new AnalyticsService(
	[googleAnalyticsTracker],
	AnalyticsCategories,
	CustomDimensions,
	AnalyticsRoutes,
	featureTogglesManager,
);

export const userStatusManager = new UserStatusManager();

const announcementReceivedService = new AnnouncementReceivedService();

const networkAnalyticsManager = new NetworkAnalyticsManager(analyticsService);

export const errorManager = new ErrorManager();

const innerAuthenticatedApiService = new AuthenticatedApiService(
	createAxiosInstance,
	cognitoAuthService,
	infraManager,
	userStatusManager,
	announcementReceivedService,
	errorManager,
);
const authenticatedApiService = new ApiServiceWithErrorHandler(
	innerAuthenticatedApiService,
	networkActionsErrorsManager,
	networkAnalyticsManager,
);

const unauthenticatedApiService = new ApiServiceWithErrorHandler(
	new UnauthenticatedApiService(createAxiosInstance, infraManager),
	networkActionsErrorsManager,
	networkAnalyticsManager,
);

export const externalApiService = new ApiServiceWithErrorHandler(
	new ExternalApiService(createAxiosInstance),
	networkActionsErrorsManager,
	networkAnalyticsManager,
);

const translationsService = new TranslationsService(() => axios.create(), serverConfigManager, masterApiService);
export const translationsManager = new TranslationsManager(
	translationsService,
	storage.getStore<Translations, Translations>(
		LocalStorageKeys.translations,
		i => i,
		i => i,
	),
);

const announcementService = new AnnouncementService(authenticatedApiService);
export const announcementManager = new AnnouncementManager(announcementService, announcementReceivedService);

export const userService = new UserService(authenticatedApiService);
export const userManager = new UserManager(cognitoAuthService, userService);

export const learnService = new LearnService(authenticatedApiService);
export const learnManager = new LearnManager(learnService, userManager);

export const tutorialBannerService = new TutorialBannerService(authenticatedApiService);
export const tutorialBannerManager = new TutorialBannerManager(tutorialBannerService, userManager);

export const userBannerManager = new UserBannerManager();

const userSurveyService = new UserSurveyService(authenticatedApiService);
export const userSurveyManager = new UserSurveyManager(userSurveyService);

const patientService = new PatientService(authenticatedApiService);
export const patientManager = new PatientManager(patientService);

const exerciseService = new ExerciseService(authenticatedApiService);
export const exerciseManager = new ExerciseManager(exerciseService);

export const libraryFilterManager = new LibraryFilterManager(exerciseManager);
export const exerciseFilterManager = new ExerciseFilterManager();

const programService = new ProgramService(authenticatedApiService);
export const programManager = new ProgramManager(programService);

const patientProgramsService = new PatientProgramsService(authenticatedApiService);
export const patientProgramsManager = new PatientProgramsManager(patientProgramsService);

const progressDataService = new ProgressDataService(authenticatedApiService);
export const progressDataManager = new ProgressDataManager(progressDataService);

const countryService = new CountryService(masterApiService, unauthenticatedApiService);
export const countryManager = new CountryManager(countryService, userManager);

export const localeManager = new LocaleManager(userManager, translationsManager, countryService);

const legalContentService = new LegalContentService(authenticatedApiService, unauthenticatedApiService);

export const legalContentManager = new LegalContentManager(
	legalContentService,
	infraManager,
	storage.getStore<LegalDocumentsMetadata, LegalDocumentsMetadata>(
		LocalStorageKeys.legalContent,
		i => i,
		i => i,
	),
);
export const analyticsDimensionsManager = new AnalyticsDimensionsManager(analyticsService, userManager, localeManager);

export const analyticsRoutesManager = new AnalyticsRoutesManager(analyticsService);
export const networkDebugManager = new NetworkDebugManager(authenticatedApiService);

const templateService = new TemplateService(authenticatedApiService);
export const templateManager = new TemplateManager(templateService);
export const templateOperationsManager = new TemplateOperationsManager(templateManager, programManager);

export const programOperationsManager = new ProgramOperationsManager(
	programManager,
	patientProgramsManager,
	templateManager,
);

export const cookiebotManager = new CookiebotManager();

const healthcareTeamService = new HealthcareTeamService(authenticatedApiService);
export const healthcareTeamManager = new HealthcareTeamManager(healthcareTeamService);

const registerService = new RegisterService(authenticatedApiService, unauthenticatedApiService);
export const registerManager = new RegisterManager(
	registerService,
	localeManager,
	cognitoAuthService,
	infraManager,
);

const veevaService = new VeevaService(masterApiService);

const vipRegisterService = new VipRegisterService(authenticatedApiService);
export const vipRegisterManager = new VipRegisterManager(
	vipRegisterService,
	veevaService,
	cognitoAuthService,
	infraManager,
);

const fastTrackRegisterService = new FastTrackRegisterService(authenticatedApiService);
export const fastTrackRegisterManager = new FastTrackRegisterManager(
	fastTrackRegisterService,
	veevaService,
	cognitoAuthService,
	infraManager,
);

const prospectRequestService = new ProspectRequestService(unauthenticatedApiService);
export const prospectRequestManager = new ProspectRequestManager(
	prospectRequestService,
	countryService,
	infraManager,
);

const notificationService = new NotificationService(authenticatedApiService);
export const notificationManager = new NotificationManager(notificationService);

export const notificationBadgeManager = new NotificationBadgeManager(
	notificationManager,
	userManager,
	storage.getStore<LastUnreadCounts, LastUnreadCounts>(
		LocalStorageKeys.notifications,
		sc => sc,
		sc => sc,
	),
);

const onboardingService = new OnboardingService(authenticatedApiService);
export const onboardingManager = new OnboardingManager(onboardingService, userManager);

export const getStartedManager = new GetStartedManager(onboardingManager, userManager);

export const onfidoService = new OnfidoService(authenticatedApiService);

const dayjsLibConfiguration = new DayjsLibConfiguration(localeManager, translationsManager);

const servicesManager = new ServicesManager([
	[serverConfigManager, "serverConfigManager", ServiceInitPriority.Critical],
	[infraManager, "infraManager", ServiceInitPriority.Critical],
	[cognitoAuthService, "cognitoAuthService", ServiceInitPriority.High],
	[localeManager, "localeManager", ServiceInitPriority.High],
	[translationsManager, "translationsManager"],
	[userManager, "userManager"],
	[dayjsLibConfiguration, "dayjsLibConfiguration"],
	[featureTogglesManager, "featureTogglesManager"],
	[onboardingManager, "onboardingManager"],
]);

export const initializeServicesAndManagers = async (): Promise<void> => {
	await servicesManager.initServices();
	await countryManager.init();
};
export const resetServicesAndManagers = async (): Promise<void> => {
	const servicesToReset = [
		patientManager,
		libraryFilterManager,
		programManager,
		patientProgramsManager,
		programOperationsManager,
		progressDataManager,
		templateManager,
		templateOperationsManager,
		localeManager,
		notificationManager,
		userStatusManager,
	];

	const resetPromises: Promise<unknown>[] = [];

	for (const service of servicesToReset) {
		const reset = (service as { reset?: () => Promise<unknown> | void }).reset;
		if (typeof reset === "function") {
			resetPromises.push(Promise.resolve(reset.call(service)));
		}
	}

	await Promise.all(resetPromises);
};
